/**
 * Fetch-basierter API-Client. Bewusst keine axios-Abhängigkeit — `fetch`
 * reicht für unsere Zwecke vollständig und spart eine 50-KB-Dependency.
 *
 * Verantwortlichkeiten:
 *  - Bearer-Token automatisch aus dem Auth-Store anhängen
 *  - Bei 401 den Auth-Store informieren (für Auto-Logout)
 *  - JSON-Encoding/Decoding zentral
 *  - Sinnvolle Fehlermeldungen statt blanker Promise-Rejections
 */

import { useAuthStore } from '@/stores/auth';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: unknown,
    message: string
  ) {
    super(message);
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
}

/**
 * Phase 21: API-Base-URL für Production-Deploys (z.B. https://api.shruby.de).
 * Im Dev leer → relative URLs gehen durch Vite-Dev-Proxy auf localhost:8080.
 */
const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const auth = useAuthStore();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (auth.token) headers['Authorization'] = `Bearer ${auth.token}`;

  let url = path;
  if (opts.query) {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(opts.query)) {
      if (v !== undefined && v !== null) params.append(k, String(v));
    }
    const qs = params.toString();
    if (qs) url += `?${qs}`;
  }

    const response = await fetch(BASE_URL + url, {
        credentials: BASE_URL ? 'omit' : 'same-origin',
        method: opts.method ?? 'GET',
        headers,
        body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      });

  // 401 → Token ungültig, Auth-Store räumt auf und Router schickt zum Login
  if (response.status === 401) {
    auth.handleUnauthorized();
    throw new ApiError(401, null, 'Nicht autorisiert — bitte neu anmelden.');
  }

  // 204 No-Content → leeres Result
  if (response.status === 204) return undefined as T;

  let body: unknown = null;
  const contentType = response.headers.get('content-type') ?? '';
  if (contentType.includes('application/json')) {
    body = await response.json();
  } else {
    body = await response.text();
  }

  if (!response.ok) {
    throw new ApiError(
      response.status,
      body,
      `Request fehlgeschlagen (${response.status})`
    );
  }
  return body as T;
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query']) =>
    request<T>(path, { method: 'GET', query }),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body }),
  del: <T>(path: string, query?: RequestOptions['query'], body?: unknown) =>
    request<T>(path, { method: 'DELETE', query, body }),
};
